import React, { useState, useEffect } from 'react'
import useTitle from '@/hooks/useTitle'
import { useNavigate } from 'react-router'
import { addIcon, userProfileIcon2 } from '@/assets/img/imgAssets'
import { PlusIcon, MinusIcon, TrashIcon, PencilSquareIcon,  } from '@heroicons/react/24/outline'
import { UserGroupIcon, BuildingOffice2Icon, ArrowTrendingUpIcon, CalendarDaysIcon } from '@heroicons/react/24/solid'
import PulseLoader from "react-spinners/PulseLoader"
import { useGetCoachUserPadsQuery } from './coachUserPadsApiSlice'
import { useGetUserPicQuery } from "../users/usersApiSlice"


const EtCoachUserPadList = ({setActiveAdminView, onAdd, onUpdate, onDelete, setSelectedEntityId }) => {
    // First Created - 03 Aug 2024
    // Author - Atul Pandey
    // Updated - 07 Aug 2024 - org Id, navigate to individual org/user/trail/session pages with edit and delete icons
    // Updated - 08 Aug 2024 - cleaned up code / comments
    // Updated - 04 Mar 2025 - chaged the hard coded coach org name, role and title
    // Updated - 04 Mar 2025 - updated the comp with props - { setActiveAdminView, onAdd, onUpdate, onDelete, setSelectedEntityId }
  // Updated - 04 Mar 2025 - switched from navigate to onAdd, onUpdate, onDelete, setSelectedEntityId

    // 0 - Set Page Title
    useTitle('Coach User Pad List')

    // 1 - set page navigations
    const navigate = useNavigate()

    // 2 - Initialize showDetails state as an object where key is the session index
    const [showCoachDetails, setShowCoachDetails] = useState({})
    const [showOrgs, setShowOrgs] = useState({})
    const [showTrails, setShowTrails] = useState({})
    const [showDetails, setShowDetails] = useState({})

    // 3 - Extract the coach user pads data
    const { data: coachUserPadsData, isError, error, isLoading, refetch } = useGetCoachUserPadsQuery()
    const coachUserPads = coachUserPadsData?.ids.map((id) => {
        const coachUserPad = coachUserPadsData?.entities[id]
        return coachUserPad
    })
    // console.log('Coach User Pads Data', coachUserPadsData)
    // console.log('Coach User Pads:', coachUserPads)

    // 3a - Refetch data when the component is mounted or navigated to
    useEffect(() => {
        refetch();
    }, [navigate, refetch]);


     // 4 - Group the data by coachId, organization, trails, sessions, and users
    const groupedData = coachUserPads?.reduce((acc, pad) => {
        const coachId = pad.coachId?.name || 'No Coach';
        const coachUsername = pad.coachId?.username || 'No Username';
        const coachOrgName = pad?.coachId?.userOrgId?.organizationName || 'No Org'; // 04 Mar 2025
        const coachOrgRole = pad?.coachId?.userOrgRole?.orgRoleName || 'EtCoach' // 04 Mar 2025
        const coachOrgTitle = pad?.coachId?.userOrgTitle?.orgTitleName || 'EtCoach' // 04 Mar 2025
        const orgId = pad.orgId?._id || 'No org id';
        const orgName = pad.userId.userOrgId?.organizationName || 'No Organization';
        const trailId=pad.trailId?._id || 'No Trail Id';
        const trailName = pad.trailId?.trailName || 'No Trail';
        const trailPic = pad.trailId?.pic || 'No Trail';
        const sessionId = pad.sessionId?._id || 'No Session';

        const session = {
            sessionId: sessionId,
            sessionName: pad.sessionId?.sessionName || 'No Session',
            sessionPic: pad.sessionId?.pic || '',
            sessionDesc: pad.sessionId?.sessionDesc || '',
            users: []
        };

        const user = {
            userId: pad.userId?._id,
            userName: pad.userId?.name,
            username: pad.userId?.username,
            userEmail: pad.userId?.email,
            ...pad // Other properties from pad if necessary
        };

        if (!acc[coachId]) 
            acc[coachId] =  { 
                username: coachUsername, 
                orgs: {},
                coachOrgName, // Added - 04 Mar 2025
                coachOrgRole, // Added - 04 Mar 2025
                coachOrgTitle // Added - 04 Mar 2025 
    
                };                                                                                                             
        if (!acc[coachId].orgs[orgId]) acc[coachId].orgs[orgId] = { name: orgName, trails: {} };
        if (!acc[coachId].orgs[orgId].trails[trailName]) acc[coachId].orgs[orgId].trails[trailName] = { id:trailId,pic: trailPic, sessions: {} }; // Updated
        if (!acc[coachId].orgs[orgId].trails[trailName].sessions[sessionId]) acc[coachId].orgs[orgId].trails[trailName].sessions[sessionId] = { ...session }; // Updated
        
        acc[coachId].orgs[orgId].trails[trailName].sessions[sessionId].users.push(user);
        
        return acc;
    }, {});

    // console.log('Grouped Data:', groupedData);

    // 5 - Toggle Show function for coach user pad details
    const handleToggleDetails = (trailIndex, sessionIndex) => {
        const key = `${trailIndex}-${sessionIndex}`;
        setShowDetails(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 5a - Toggle Show function for trails
    const handleToggleCoachDetails = (coachId) => {
        setShowCoachDetails(prev => ({ ...prev, [coachId]: !prev[coachId] }));
    }
    
    // 5b - Toggle Show function for trails
    const handleToggleTrails = (orgIndex, trailIndex) => {
        const key = `${orgIndex}-${trailIndex}`;
        setShowTrails(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // 5b - Toggle Show function for trails
    const handleToggleOrgs = (coachIndex, orgIndex) => {
        const key = `${coachIndex}-${orgIndex}`;
        setShowOrgs(prev => ({ ...prev, [key]: !prev[key] }));
    };
    
    if (isLoading) return <PulseLoader color={"#ea580c"} />
    if (isError) return <p className="text-red-500">{error?.data?.message}</p>

    return (
        <div className='bg-gray-200 m-1 p-1 rounded font-poppins mx-auto'>
            {/* Header */}
            <div className='flex justify-start items-center m-1 p-1'>
                <div><p className='text-2xl m-1 p-1 font-bold'>Et Coach User Pads</p></div>
                <div>
                    <img
                        src={addIcon}
                        alt=""
                        className="h-10 w-10 cursor-pointer"
                        // onClick={() => navigate('/coachUserPad/create')}
                        onClick={onAdd} // 04 Mar 2025
                    />
                </div>
            </div>

           
            {Object.keys(groupedData)?.length === 0 ? (
                <div className="m-1 p-1 bg-white rounded">
                    <p className='text-orange-600 text-xs m-1 p-1'>No coach user pads available. Please create one by clicking the "+" button above.</p>
                </div>
            ) : (
            Object.entries(groupedData)?.map(([coachId, orgs], coachIndex) => {
                // const { username: coachUsername, orgs: organizations } = orgs;
                const { username: coachUsername, orgs: organizations, coachOrgName, coachOrgRole, coachOrgTitle } = orgs; // 04 Mar 2025
                const orgCount = Object.entries(organizations)?.length
                const trailCount = Object.values(organizations).reduce((acc, org) => acc + Object.keys(org.trails).length, 0);
                const sessionCount = Object.values(organizations).reduce((acc, org) => {
                    return acc + Object.values(org.trails).reduce((trailAcc, trail) => trailAcc + (trail?.sessions ? Object.keys(trail.sessions).length : 0), 0); // Updated
                }, 0);
                
                const uniqueUserIds = new Set(); // 05 Aug 2024
                Object.values(organizations).forEach(org => {
                    Object.values(org.trails).forEach(trail => {
                        Object.values(trail.sessions || {}).forEach(session => {
                            session.users.forEach(user => uniqueUserIds.add(user.userId._id));
                        });
                    });
                });
                
                const userCount = uniqueUserIds.size; // 05 Aug 2024
                return(
                <div key={coachIndex} className='m-1 p-1 bg-white rounded'>
                    <div className='m-1 p-1 bg-gray-300 rounded '>
                        {/* Coach Header */}
                        <div className='grid grid-cols-11 justify-between items-center m-1 p-1'>
                            <div className='col-span-3 flex justify-start items-center m-1 p-1'>
                                <div onClick={() => handleToggleCoachDetails(coachIndex)} className="mx-2 rounded-full h-7 w-7 hover:bg-white flex justify-center items-center cursor-pointer">{showCoachDetails[coachIndex] ? <MinusIcon className="h-5 w-5 text-orange-600" /> : <PlusIcon className="h-5 w-5 text-orange-600" />}</div>
                                <div>
                                    <User
                                        username={coachUsername}
                                        defaultUserPic={userProfileIcon2}
                                    />
                                </div>
                                <div>
                                    <p className="px-2 mx-2 text-gray-600 font-bold">{coachId}</p>
                                    {/* <p className='mx-2 text-[10px] text-gray-400 rounded-full inline-block px-2 border bg-white shadow shadow-gray-400'>Founder, <span className='text-orange-600'>Eternity Solutions</span></p> */}

                                     {/* 04 Mar 2025*/}
                                     <p className='mx-2 text-[10px] text-gray-400 rounded-full inline-block px-2 border bg-white shadow shadow-gray-400'>{coachOrgRole} & {coachOrgTitle}, <span className='text-orange-600'>{coachOrgName}</span></p> 
                                </div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Organizations :</p></div>
                                <div>
                                            <BuildingOffice2Icon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{orgCount}</p></div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Trails :</p></div>
                                <div>
                                            <ArrowTrendingUpIcon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{trailCount}</p></div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Sessions :</p></div>
                                <div>
                                            <CalendarDaysIcon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{sessionCount}</p></div>
                            </div>
                            <div className='col-span-2 flex justify-start items-center'>
                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                <div>
                                            <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                </div>
                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                            </div>
                        </div>
                    

                    {/* Coach Details */}
                    {showCoachDetails[coachIndex] && Object.entries(organizations)?.map(([orgId, orgData], orgIndex) => {
                        const { name: orgName, trails } = orgData; // Extract orgName and trails
                        const trailCount = Object.keys(trails)?.length;
                        const sessionCount = Object.values(trails).reduce((acc, trail) => acc + (trail?.sessions ? Object.keys(trail.sessions).length : 0), 0); // Ensure sessions is defined
                        const uniqueUserIds = new Set(); // 05 Aug 2024
                        Object.values(trails).forEach(trail => {
                            Object.values(trail?.sessions).forEach(session => {
                                session.users.forEach(user => uniqueUserIds.add(user.userId._id));
                            });
                        });
                        const userCount = uniqueUserIds.size;// 05 Aug 2024
                        return(
                        <div key={orgIndex} className="bg-white rounded m-1 p-1">
                            {/* Org Header */}
                            <div className='bg-gray-300 rounded m-1 p-1'>
                                <div className='grid grid-cols-10 gap-1 justify-start items-center m-1 p-1'>
                                    <div className='col-span-3 flex justify-start items-center m-1 p-1'>
                                        <div onClick={() => handleToggleOrgs(coachIndex, orgIndex)} className="mx-2 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer">{showOrgs[`${coachIndex}-${orgIndex}`] ? <MinusIcon className="h-4 w-4 text-orange-600" /> : <PlusIcon className="h-4 w-4 text-orange-600" />}</div>
                                        <div><p className="font-semibold">{orgName}</p></div>
                                    </div>
                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Trails :</p></div>
                                        <div>
                                            <ArrowTrendingUpIcon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>
                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{trailCount}</p></div>
                                    </div>
                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Sessions :</p></div>
                                        <div>
                                            <CalendarDaysIcon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>
                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{sessionCount}</p></div>
                                    </div>
                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                        <div>
                                            <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                        </div>
                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                                    </div>
                                </div>
                                
                                {/* Org Details */}
                                {showOrgs[`${coachIndex}-${orgIndex}`] && Object.entries(trails)?.map(([trailName, trailData], trailIndex) => {
                                    const sessionCount = trailData.sessions ? Object.keys(trailData?.sessions).length : 0; // Ensure sessions is defined
                                    const uniqueUserIds = new Set(); // 05 Aug 2024
                                    Object.values(trailData.sessions || {}).forEach(session => {
                                        session?.users.forEach(user => uniqueUserIds.add(user.userId._id));
                                    });
                                    const userCount = uniqueUserIds.size; // 05 Aug 2024
                                    
                                    return(
                                    <div key={trailIndex} className='bg-gray-100 rounded m-1 p-1'>
                                        {/* Trail Header */}
                                        <div className='grid grid-cols-9 justify-between items-center m-1 p-1'>

                                            <div className='col-span-3 flex justify-start items-center m-1 p-1'>
                                                <div onClick={() => handleToggleTrails(orgIndex, trailIndex)} className="mx-2 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer">{showTrails[`${orgIndex}-${trailIndex}`] ? <MinusIcon className="h-4 w-4 text-orange-600" /> : <PlusIcon className="h-4 w-4 text-orange-600" />}</div>
                                                <div className='mx-2 flex justify-center items-center rounded-full bg-gray-100 hover:bg-white h-8 w-8'>
                                                    <img src={trailData.pic} alt="" className='h-6 w-6' />
                                                </div>
                                                <div><p className="font-semibold">{trailName}</p></div>
                                            </div>
                                            <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Sessions :</p></div>
                                                <div>
                                                    <CalendarDaysIcon className='h-5 w-5 text-orange-600 m-1'/>
                                                </div>
                                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{sessionCount}</p></div>
                                            </div>
                                            <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                                <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                                <div>
                                                    <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                                </div>
                                                <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                                            </div>
                                        </div>

                                        {showTrails[`${orgIndex}-${trailIndex}`] && Object.entries(trailData.sessions || {})?.map(([sessionId, sessionData], sessionIndex) => {                                       
                                            const userCount = sessionData.users ? sessionData.users.length : 0; //08 Aug 2024
                                            const trailId = trailData.id
                                            
                                            return(
                                            <div key={sessionId+sessionIndex} className="m-1 p-1 bg-gray-200 rounded">

                                                <div className='grid grid-cols-12 justify-between items-center m-1 p-1'>
                                                    <div className='col-span-6 flex justify-start items-center'>
                                                        <div onClick={() => handleToggleDetails(trailIndex, sessionIndex)} className="mx-2 rounded-full h-6 w-6 hover:bg-white flex justify-center items-center cursor-pointer">{showDetails[`${trailIndex}-${sessionIndex}`] ? <MinusIcon className="h-4 w-4 text-orange-600" /> : <PlusIcon className="h-4 w-4 text-orange-600" />}</div>
                                                        <div className='mx-2 flex justify-center items-center rounded-full bg-gray-100 hover:bg-white h-8 w-8'>
                                                            <img src={sessionData?.sessionPic} alt="" className='h-6 w-6' />
                                                        </div>
                                                        <div><p className="text-xs font-semibold">Session {sessionIndex +1} - {sessionData?.sessionName}</p></div>
                                                    </div>
                                                    <div className='col-span-2 flex justify-start items-center m-1 p-1'>
                                                        <div><p className='text-[10px] text-gray-500 mx-2 px-2'>Users :</p></div>
                                                        <div>
                                                            <UserGroupIcon className='h-5 w-5 text-orange-600 m-1'/>
                                                        </div>
                                                        <div className='rounded-full flex justify-center items-center h-5 w-5 border border-orange-300 bg-gray-50'><p className='text-orange-600 text-[10px]'>{userCount}</p></div>
                                                    </div>
                                                    <div className='col-span-4 flex justify-end items-center mx-2'>
                                                        <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-white'>
                                                            <PencilSquareIcon 
                                                            // onClick={() => navigate(`/coachUserPad/update/${orgId}/${trailId}/${sessionId}`)} 
                                                            // onClick={() => onUpdate(sessionId)} // 04 Mar 2025 
                                                                       
                                                            className="h-4 w-4 text-orange-600 cursor-pointer" />
                                                        </div>
                                                        <div className='rounded-full flex justify-center items-center h-6 w-6 hover:bg-white'>
                                                            <TrashIcon 
                                                            // onClick={() => navigate(`/coachUserPad/delete/${orgId}/${trailId}/${sessionId}`)} 
                                                            // onClick={() => onDelete(sessionId)} // 04 Mar 2025
                                                            
                                                            
                                                            className="h-4 w-4 text-orange-600 cursor-pointer" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="m-1 p-1">
                                                    {showDetails[`${trailIndex}-${sessionIndex}`] && sessionData.users?.map((user, userIndex) => {
                                                        
                                                        return(
                                                        <div key={userIndex} className='m-1 p-1 bg-gray-50 rounded'>
                                                            <div className='flex justify-between items-center m-1 p-1'>
                                                                <div className=' flex justify-start items-center m-1 p-1'>
                                                                    <div className='mx-1 px-1'>
                                                                        <User
                                                                        username={user.username}
                                                                        defaultUserPic={userProfileIcon2}
                                                                    />
                                                                    </div>
                                                                    <div className='mx-1 px-1'>
                                                                        <p className='text-xs text-gray-600 font-bold'>{user.userName}</p>
                                                                        <p className='text-[10px] text-gray-400'>{user.userEmail}</p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )})}
                                                </div>
                                            </div>
                                        )})}
                                    </div>
                                )})}
                            </div>
                        </div>
                    )})}
                    
                    </div>

                </div>
            )}))}
        </div>
    )
}

const User = ({ username, defaultUserPic }) => {
    const { data: userPic, isFetching: isFetchingUserPic } =
      useGetUserPicQuery(username)
  
    return (
      <div className="flex justify-center items-center h-12 w-12 rounded-full border border-white bg-gradient-to-r from-orange-600 to-orange-400 shadow-lg shadow-black cursor-pointer">
        {/* Display user picture or loading indicator*/}
        {isFetchingUserPic ? (
          <div>Loading...</div>
        ) : userPic ? (
          <img src={userPic} alt="User" className="h-11 w-11  rounded-full" />
        ) : (
          <img src={defaultUserPic} alt="User" className="h-10 w-10 p-1" />
        )}
      </div>
    )
  }


export default EtCoachUserPadList